import { useEffect, useRef, useState } from 'react'

type UseSubmitResultParams = {
  isRequesting: boolean
  errorMessage: string
  onSuccess: () => void
  onError: () => void
}

/**
 * API送信結果（成功／失敗）を1送信につき1回だけハンドリングする共通フック。
 * clearErrorMessage 由来の中間レンダーでの成功誤判定（モーダル早期クローズ）を防ぐため、
 * resultHandledRef で「結果処理は1回だけ」を保証する。
 *
 * - handleSubmit(): 送信開始時に呼び、結果ハンドリングを有効化する
 * - resetSubmit(): 送信前にモーダルを閉じる等で結果ハンドリングを破棄する
 */
const useSubmitResult = ({ isRequesting, errorMessage, onSuccess, onError }: UseSubmitResultParams) => {
  const [submitted, setSubmitted] = useState(false)
  const resultHandledRef = useRef(false)
  // 最新のコールバックを参照し、依存配列の変化による不要な再評価を避ける
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  onSuccessRef.current = onSuccess
  onErrorRef.current = onError

  useEffect(() => {
    if (!submitted || isRequesting || resultHandledRef.current) {
      return
    }
    resultHandledRef.current = true
    if (errorMessage === '') {
      onSuccessRef.current()
    } else {
      onErrorRef.current()
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage])

  const handleSubmit = () => {
    resultHandledRef.current = false
    setSubmitted(true)
  }

  const resetSubmit = () => {
    setSubmitted(false)
  }

  return { handleSubmit, resetSubmit }
}

export default useSubmitResult
